import {
  Component,
  EventEmitter,
  forwardRef,
  inject,
  Injector,
  Input,
  Output,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormsModule,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  NgControl,
  ReactiveFormsModule,
  ValidationErrors,
  Validator,
} from '@angular/forms';
import { MatFormFieldAppearance, MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { ContractStatusLabelPipe } from '../contract-status-label-pipe';
import { ContractStatus, contractStatusOrdered } from '../contract.model';

@Component({
  selector: 'app-multi-select-contract-status',
  imports: [
    MatFormFieldModule,
    MatSelectModule,
    FormsModule,
    ReactiveFormsModule,
    ContractStatusLabelPipe,
  ],
  templateUrl: './multi-select-contract-status.html',
  styleUrls: ['./multi-select-contract-status.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MultiSelectContractStatus),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => MultiSelectContractStatus),
      multi: true,
    },
  ],
})
export class MultiSelectContractStatus implements ControlValueAccessor, Validator {
  private injector = inject(Injector);

  @Input() appearance: MatFormFieldAppearance = 'fill';
  @Input() label = 'Statut du contrat';
  @Input() values: ContractStatus[] | undefined;
  @Input() required = false;
  @Output() selectionChange = new EventEmitter<ContractStatus[]>();
  public ngControl?: NgControl;
  disabled = false;

  ContractStatuses = contractStatusOrdered;

  ngOnInit(): void {
    this.ngControl = this.injector.get(NgControl, null) ?? undefined;
    if (this.ngControl) {
      this.required = this.hasRequiredValidator(this.ngControl.control ?? null);
      try {
        this.ngControl.valueAccessor = this;
      } catch {
        console.warn(
          'Impossible de définir le valueAccessor pour MultiSelectStatus. Assurez-vous que ce composant est utilisé dans un contexte de formulaire (FormGroup, FormControl, etc.)',
        );
      }
    }
  }

  onChange: (values: ContractStatus[] | undefined) => void = () => {
    // No-op
  };
  onTouched: () => void = () => {
    // No-op
  };

  writeValue(values: ContractStatus[] | undefined): void {
    this.values = values;
  }

  registerOnChange(fn: (values: ContractStatus[] | undefined) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  validate(control: AbstractControl): ValidationErrors | null {
    const ctrl = control ?? this.control;
    const isRequired = this.required || (ctrl ? this.hasRequiredValidator(ctrl) : false);

    if (isRequired && !this.values?.length) {
      return { required: true };
    }

    return null;
  }

  private hasRequiredValidator(control: AbstractControl | null | undefined): boolean {
    if (!control || !control.validator) return false;

    const validator = control.validator({} as AbstractControl);
    return !!(validator && validator['required']);
  }

  get control(): AbstractControl | null {
    return this.ngControl ? (this.ngControl.control ?? null) : null;
  }

  onSelectionChange(values: ContractStatus[]): void {
    this.values = values;
    this.onChange(this.values);
    this.onTouched();
    this.selectionChange.emit(values);
  }
}
